import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { RootLayout } from '@/routes/root';
import { LoginPage } from '@/routes/login';
import { ForgotPasswordPage } from '@/routes/forgot-password';
import { HomePage } from '@/routes/home';
import { NewPatientPage } from '@/routes/new-patient';
import { ShiftTabsPage } from '@/routes/shift-tabs';
import { CurrentShiftPage } from '@/routes/current-shift';
import { ProfilePage } from '@/routes/profile';
import { HistoryPage } from '@/routes/history';
import { FavoritesPage } from '@/routes/favorites';
import { UpdatePrompt } from '@/components/UpdatePrompt';

// Lazy-load the patient detail and acts flow — these pull in the large
// ICD-10 list (~400 kB) and the act catalog (~120 kB). Keeps initial bundle
// small for the auth + dashboard path that most opens take.
const PatientDetailPage = lazy(() =>
  import('@/routes/patient').then((m) => ({ default: m.PatientDetailPage })),
);
const ActCategoriesPage = lazy(() =>
  import('@/routes/acts/categories').then((m) => ({
    default: m.ActCategoriesPage,
  })),
);
const ActSubCategoriesPage = lazy(() =>
  import('@/routes/acts/sub-categories').then((m) => ({
    default: m.ActSubCategoriesPage,
  })),
);
const ActListPage = lazy(() =>
  import('@/routes/acts/list').then((m) => ({ default: m.ActListPage })),
);
const ActFormPage = lazy(() =>
  import('@/routes/acts/form').then((m) => ({ default: m.ActFormPage })),
);

// Dev-only impersonation page. Gated on import.meta.env.DEV so Vite/Rollup
// statically eliminates both the lazy() call and the dynamic import target
// from the production build — verified via `grep impersonate dist/assets/*`.
const DevImpersonatePage = import.meta.env.DEV
  ? lazy(() =>
      import('@/routes/dev/impersonate').then((m) => ({
        default: m.DevImpersonatePage,
      })),
    )
  : null;

function SplashScreen() {
  return (
    <div className="min-h-dvh bg-brand-darkest text-white flex items-center justify-center">
      <div className="text-white/70 text-sm">Chargement…</div>
    </div>
  );
}

export function App() {
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

  if (isInitializing) return <SplashScreen />;

  return (
    <BrowserRouter>
      <UpdatePrompt />
      <Suspense fallback={<SplashScreen />}>
        <Routes>
          {DevImpersonatePage && (
            <Route path="/dev/impersonate" element={<DevImpersonatePage />} />
          )}
          {!isLoggedIn ? (
            <>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <Route element={<RootLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/new-patient" element={<NewPatientPage />} />
              <Route path="/shift" element={<ShiftTabsPage />} />
              <Route path="/shift/end" element={<CurrentShiftPage />} />
              <Route path="/patient/:id" element={<PatientDetailPage />} />
              <Route path="/patient/:id/acts" element={<ActCategoriesPage />} />
              <Route
                path="/patient/:id/acts/:cat"
                element={<ActSubCategoriesPage />}
              />
              <Route
                path="/patient/:id/acts/:cat/:sub"
                element={<ActListPage />}
              />
              <Route
                path="/patient/:id/acts/:cat/:sub/:act"
                element={<ActFormPage />}
              />
              <Route path="/favorites" element={<FavoritesPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
